import React from 'react';
import moment from 'moment';
import {
    AddButton,
    AddCircleBlue,
    cardStyle,
    CustomizedSwitch,
    EditWithIcon,
    MinLoader,
    PriceRow,
    SimpleTooltip,
    StatusCard,
    TextRow,
    TypeRow,
} from 'components';
import { ActiveInactiveStatusReverse, Colors, Images, makeCapitalize, PermissionList, RolePermission } from 'utils';
import { FundingSourceModifiersAdd } from './modals';
// Direct file import, not the `pages` barrel: the schedule staff picker imports this file, and the barrel creates a load-order cycle.
import { payerTypeOptions } from '../../../../pages/fundingSource/constants';
import { clearHouseTypeEnums } from '../../../system/core/constants';

/**
 * How a charge rate's credential list is matched against a staff member's credentials.
 * DUPLICATED from CredentialMatchMode in @therapylake/package (chargeRate.constants.ts) — the client
 * cannot import it (see MAX_AUTHORIZATION_ID_LENGTH in utils/constants.js).
 */
export const CredentialMatchMode = {
    ALL: 'ALL',
    ANY: 'ANY',
};

export function getFundingSourceGeneralInfo(info) {
    return [
        { title: 'Name', value: makeCapitalize(info?.name) },
        { title: 'Email Address', value: info?.email },
        {
            title: 'Phone Number',
            value: info?.phoneNumber
                ? '(' + info?.phoneNumber.substr(0, 3) + ') ' + info?.phoneNumber.substr(3, 3) + '-' + info?.phoneNumber.substr(6, 6)
                : '',
        },
        { title: 'Status', value: ActiveInactiveStatusReverse(info?.status) },
    ].filter((item) => !!item.value);
}

export function getFundingSourceContactInfo(info) {
    const classes = cardStyle();

    return [
        { title: 'Type', value: payerTypeOptions?.find((i) => i?.id === info?.type)?.name },
        { title: 'Contact Title', value: info?.contactTitle },
        { title: 'Contact Email', value: info?.contactEmail },
        { title: 'Contact Person', value: info?.contact },
        {
            type: 'renderValue',
            value: (
                <div>
                    {info?.payerIds?.length ? (
                        <p className={classes.titleStyle}>Payer IDs</p>
                    ) : (
                        <div className="flex-align-center">
                            <p className={classes.titleStyle}>Payer IDs:</p>
                            <span className={classes.cardValue} style={{ marginTop: 0 }}>
                                N/A
                            </span>
                        </div>
                    )}
                    {info?.payerIds?.map((i, j) => (
                        <div key={j} className="flex-align-center">
                            <p style={{ fontWeight: 400 }} className={classes.titleStyle}>
                                {i?.clearinghouse === clearHouseTypeEnums?.TL_CLEARINGHOUSE
                                    ? ' TL Clearinghouse'
                                    : i?.clearinghouse === clearHouseTypeEnums?.AVAILITY
                                      ? ' Availity'
                                      : i?.clearinghouse === clearHouseTypeEnums?.OFFICE_ALLY
                                        ? ' Office Ally'
                                        : 'Payer ID'}
                                :{' '}
                            </p>

                            <span className={classes.cardValue} style={{ marginTop: 0 }}>
                                {i?.id ? i?.id : '--'}
                            </span>
                        </div>
                    ))}
                </div>
            ),
        },
        { title: 'Short Name ', value: info?.shortName },
        { title: 'Vendor ID', value: info?.vendor },
        { title: 'Website', value: info?.website ? info?.website : null, type: 'link' },
    ].filter((item) => !!item.value);
}

export function getFundingSourceClients(info) {
    const fullData = [
        {
            type: 'Total Clients',
            count: info?.totalCount === 0 ? '0' : info?.totalCount,
        },
        ...info?.clients,
    ];
    const updatedData = fullData?.map((item) => ({
        title: item.type === 'FBA' ? 'FBA' : makeCapitalize(item.type),
        value: item.count,
    }));

    return updatedData.filter((item) => !!item.value);
}

export function getFundingSourceAddressInfo(info) {
    return [
        { title: 'Street Address', value: info?.address?.street },
        { title: 'Country', value: info?.address?.country },
        { title: 'City', value: info?.address?.city },
        { title: 'State', value: info?.address?.state },
        { title: 'Zip Code', value: info?.address?.zip },
        { title: 'Unit/Suite #', value: info?.address?.unit ? info?.address?.unit : 'Not Set' },
    ].filter((item) => !!item.value);
}

export const serviceStatusEnums = {
    ACTIVE: 'ACTIVE',
    INACTIVE: 'INACTIVE',
};

// Funding Source Service Table config
export const fundingSourceServiceHead = [
    { name: 'serviceTypeId', rowName: 'name', title: 'Service', custom: false, noSearch: true },
    { name: 'cptCode', title: 'CPT Code', custom: false, noSearch: true },
    {
        name: 'type',
        title: 'Type',
        disabled: true,
        noSearch: true,
        filterList: [
            { name: 'All', id: 'All' },
            { name: 'Direct', id: 'DIRECT' },
            { name: 'Indirect', id: 'INDIRECT' },
        ],
    },
    { name: 'unitSize', title: 'Unit Size (min)', custom: false, noSearch: true },
    { name: 'min', title: 'Min Unit', custom: false, noSearch: true },
    { name: 'max', title: 'Max Unit', custom: false, noSearch: true },
    { name: '', title: 'Diagnostic Codes', custom: false, noSearch: true },
    {
        name: 'status',
        title: 'Status ',
        disabled: true,
        noSearch: true,
        filterList: [
            { name: 'All', id: 'All' },
            { name: 'Active', id: serviceStatusEnums.ACTIVE },
            { name: 'Inactive', id: serviceStatusEnums.INACTIVE },
        ],
    },
    RolePermission([PermissionList.FS_SERVICE_CREATE?.code]) && {
        name: 'action',
        title: 'Action',
        custom: false,
        disabled: true,
        noSearch: true,
        width: 210,
    },
];

export const fundingSourceServiceBody = [
    { rowText: (item) => <TextRow name={item?.serviceTypeId?.name} textWidth={8} /> },
    { rowText: (item) => <TextRow name={item?.cptCode} /> },
    { rowText: (item) => <TypeRow text={item?.type ? item?.type : 'Not Set'} /> },
    { rowText: (item) => <TextRow name={item?.unitSize} /> },
    { rowText: (item) => <TextRow name={item?.min} /> },
    { rowText: (item) => <TextRow name={item?.max} /> },

    {
        rowText: (item) =>
            item?.diagnosticCodes?.length ? (
                <div className="credential-item">
                    <div className="diagnostic-code-chip">{item?.diagnosticCodes[0]}</div>
                    {item?.diagnosticCodes?.length > 1 ? (
                        <SimpleTooltip
                            title={
                                <div>
                                    {item?.diagnosticCodes?.slice(1)?.map((item, k) => (
                                        <p key={k}>{item}</p>
                                    ))}
                                </div>
                            }
                            placement="top-end"
                        >
                            {item?.diagnosticCodes?.length > 1 ? <button>+{item?.diagnosticCodes?.length - 1}</button> : ''}
                        </SimpleTooltip>
                    ) : (
                        ''
                    )}
                </div>
            ) : (
                <p className="not-set">Not set</p>
            ),
    },
    {
        rowText: (item) => <StatusCard status={item?.active ? serviceStatusEnums.ACTIVE : serviceStatusEnums.INACTIVE} />,
    },

    RolePermission([PermissionList.FS_SERVICE_CREATE?.code]) && {
        button: (fn, item) => (
            <div style={{ display: 'flex', alignItems: 'center' }}>
                {RolePermission([PermissionList.FS_SERVICE_CREATE?.code]) && (
                    <EditWithIcon
                        handleClick={(e) => {
                            fn('editFundingSourseService');
                        }}
                    />
                )}
                <AddButton
                    styles={{ marginLeft: '16px', padding: '9px 16px', whiteSpace: 'nowrap' }}
                    Icon={false}
                    text={'Charge Rates'}
                    handleClick={() => fn('openModifiersDrawer')}
                />
            </div>
        ),
        notClickable: true,
    },
];

export const ACTION_TYPE = 'GET_FUNDING_SOURCE_SERVICE_BY_ID';

export const FS_STATUSES = [
    { label: 'Active', id: 'ACTIVE' },
    { label: 'Inactive', id: 'INACTIVE' },
];

export const serviceNotYet = {
    title: 'No Services Added',
    subTitle: 'Add services to this funding source.',
    image: Images.noService,
};

export const serviceChargeRateHead = [
    { name: '', title: 'Title', custom: false, noSearch: true },
    { name: '', title: 'Modifier 1', custom: false, noSearch: true },
    { name: '', title: 'Modifier 2', custom: false, noSearch: true },
    { name: '', title: 'Modifier 3', custom: false, noSearch: true },
    { name: '', title: 'Credentials', custom: false, noSearch: true },
    { name: '', title: 'Billing Rate', custom: false, noSearch: true },
    { name: '', title: 'Allowed Rate', custom: false, noSearch: true },
    { name: '', title: 'Type', custom: false, noSearch: true, width: '90px' },
    { name: '', title: 'Default Paycode', custom: false, noSearch: true },
    { name: '', title: 'Status', custom: false, noSearch: true },
    RolePermission([PermissionList.FS_SERVICE_CREATE?.code]) && { name: '', title: 'Action', custom: false, noSearch: true, width: '50px' },
];

/** Full credential list for a charge rate, headed by how it is matched (all vs any). */
const ChargeRateCredentialsTooltip = ({ chargeRate }) => (
    <div>
        <p style={{ fontWeight: 600 }}>
            {chargeRate?.credentialMatch === CredentialMatchMode.ANY ? 'Staff needs any of:' : 'Staff needs all of:'}
        </p>
        {chargeRate?.credentials?.map((credential, k) => (
            <p key={k}>{credential?.name}</p>
        ))}
    </div>
);

export const serviceChargeRateBody = (currentId) => [
    { rowText: (item) => <TextRow name={item?.name} /> },
    { rowText: (item) => <TextRow name={item?.modifierTag ? item?.modifierTag : '--'} /> },
    { rowText: (item) => <TextRow name={item?.modifierTag2 ? item?.modifierTag2 : '--'} /> },
    { rowText: (item) => <TextRow name={item?.modifierTag3 ? item?.modifierTag3 : '--'} /> },
    {
        rowText: (item) =>
            item?.credentials?.length ? (
                <div className="credential-item">
                    <TextRow name={item?.credentials?.length ? item?.credentials[0]?.name : ''} textWidth={8} />
                    {item?.credentials?.length > 1 ? (
                        <SimpleTooltip
                            title={<ChargeRateCredentialsTooltip chargeRate={item} />}
                            placement="top-end"
                        >
                            {item?.credentials?.length > 1 ? <button>+{item?.credentials?.length - 1}</button> : ''}
                        </SimpleTooltip>
                    ) : (
                        ''
                    )}
                </div>
            ) : (
                <p className="not-set" style={{ whiteSpace: 'nowrap' }}>
                    Not set
                </p>
            ),
    },
    { rowText: (item) => <PriceRow info={item?.billedRate ? item?.billedRate : 0} /> },
    { rowText: (item) => <PriceRow info={item?.allowedRate ? item?.allowedRate : 0} /> },
    { rowText: (item) => <TypeRow text={item?.type ? item?.type : 'Not Set'} /> },
    { rowText: (item) => <TextRow name={item?.payCodeTypeName ? item?.payCodeTypeName : 'Not Set'} /> },
    {
        button: (fn, item) => (
            <div className="flex-align-center" style={{ gap: 8 }}>
                <StatusCard
                    status={item?.status === true ? serviceStatusEnums.ACTIVE : serviceStatusEnums.INACTIVE}
                    text={item?.status === false ? `: ${moment.utc(item?.inactivatedDate).format('MM/DD/YY')}` : ''}
                />
                {item?.status && (
                    <>
                        {currentId === item?.id ? (
                            <MinLoader margin={'0'} color={Colors.ThemeBlue} position={'relative'} />
                        ) : (
                            <CustomizedSwitch
                                disabled={!RolePermission([PermissionList.FS_SERVICE_CREATE?.code])}
                                checked={item?.status}
                                handleClick={() => fn('changeStatus')}
                            />
                        )}
                    </>
                )}
            </div>
        ),
    },
    RolePermission([PermissionList.FS_SERVICE_CREATE?.code]) && {
        button: (fn, item) => <EditWithIcon handleClick={() => fn('editFundingSourceService')} />,
    },
];

export const SERVICE_CHARGE_RATE_ACTION_TYPE = 'GET_FUNDING_MODIFIERS';

export const chargeRateNotYet = {
    title: 'No charge rates yet',
    subTitle: 'Add charge rate to this service.',
};

/** Helper under the charge rate modal's "Require all credentials" toggle (on = ALL, off = ANY). */
export const credentialMatchHelperText = {
    [CredentialMatchMode.ALL]: 'Staff must have all selected credentials.',
    [CredentialMatchMode.ANY]: 'Staff must have at least one selected credential.',
};

export const supportEnums = {
    SUPPORTED: 'SUPPORTED',
    NOT_SUPPORTED: 'NOT_SUPPORTED',
    ENROLLMENT_REQUIRED: 'ENROLLMENT_REQUIRED',
};
export const payerCapabilitiesHeader = (listInfo) => {
    const {
        claimPayment,
        claimStatus,
        coordinationOfBenefits,
        dentalClaimSubmission,
        eligibilityCheck,
        institutionalClaimSubmission,
        professionalClaimSubmission,
        unsolicitedClaimAttachment,
    } = listInfo?.stats?.transactionSupport || {};

    return [
        {
            name: 'Name & ID',
            width: '14%',
            search: true,
            searchName: 'displayName',
            total: { name: 'total payers', count: listInfo?.stats?.total || '0' },
        },
        { name: 'Eligibility checks', width: '11%', subInfo: eligibilityCheck?.supported || '0' },
        { name: 'COB check', width: '10%', subInfo: coordinationOfBenefits?.supported || '0' },
        {
            name: 'Claim submission',
            width: '13%',
            subInfo: `${professionalClaimSubmission?.supported || '0'}P, ${institutionalClaimSubmission?.supported || '0'}I, ${dentalClaimSubmission?.supported || '0'}D`,
        },
        { name: 'Claim acknowledgment', width: '15%', subInfo: `${professionalClaimSubmission?.supported || '0'}CA` },
        { name: 'Claim payment', width: '11%', subInfo: `${claimPayment?.supported || '0'}ERA` },
        { name: 'Claim attachments', width: '12%', subInfo: unsolicitedClaimAttachment?.supported || '0' },
        { name: 'Real-time claim status', width: '14%', subInfo: claimStatus?.supported || '0' },
    ];
};

export const payerCapabilitiesBody = (item) => {
    return [
        {
            rowValue: (
                <div className="full-width">
                    <TextRow name={item?.payer?.displayName} fontWeight={600} wrapperStyle={{ lineHeight: '18px' }} textWidth={7} />
                    {item?.payer?.primaryPayerId && (
                        <TextRow
                            name={item?.payer?.primaryPayerId}
                            wrapperStyle={{ fontSize: 14, color: '#475467', marginTop: 4, fontWeight: 600 }}
                        />
                    )}
                </div>
            ),
            width: '14%',
            search: true,
            searchName: true,
            total: { name: 'total payers' },
        },
        {
            rowValue: (
                <div className="flex-align-center" style={{ height: '45px' }}>
                    <img
                        src={
                            item?.payer?.transactionSupport?.eligibilityCheck === supportEnums.SUPPORTED
                                ? Images.checkCircleBlue
                                : item?.payer?.transactionSupport?.eligibilityCheck === supportEnums.ENROLLMENT_REQUIRED
                                  ? Images.checkCircleBlueTransparent
                                  : Images.closeGray
                        }
                        alt="checked"
                    />
                </div>
            ),
            width: '11%',
        },
        {
            rowValue: (
                <div className="flex-align-center" style={{ height: '45px' }}>
                    <img
                        src={
                            item?.payer?.transactionSupport?.coordinationOfBenefits === supportEnums.SUPPORTED
                                ? Images.checkCircleBlue
                                : item?.payer?.transactionSupport?.coordinationOfBenefits === supportEnums.ENROLLMENT_REQUIRED
                                  ? Images.checkCircleBlueTransparent
                                  : Images.closeGray
                        }
                        alt="checked"
                    />
                </div>
            ),
            width: '10%',
        },
        {
            rowValue:
                item?.payer?.transactionSupport?.institutionalClaimSubmission === supportEnums.SUPPORTED ||
                item?.payer?.transactionSupport?.institutionalClaimSubmission === supportEnums.ENROLLMENT_REQUIRED ||
                item?.payer?.transactionSupport?.dentalClaimSubmission === supportEnums.SUPPORTED ||
                item?.payer?.transactionSupport?.dentalClaimSubmission === supportEnums.ENROLLMENT_REQUIRED ||
                item?.payer?.transactionSupport?.professionalClaimSubmission === supportEnums.SUPPORTED ||
                item?.payer?.transactionSupport?.professionalClaimSubmission === supportEnums.ENROLLMENT_REQUIRED ? (
                    <div className="flex-align-center" style={{ height: '45px', gap: '8px' }}>
                        {(item?.payer?.transactionSupport?.institutionalClaimSubmission === supportEnums.SUPPORTED ||
                            item?.payer?.transactionSupport?.institutionalClaimSubmission === supportEnums.ENROLLMENT_REQUIRED) && (
                            <img
                                src={
                                    item?.payer?.transactionSupport?.institutionalClaimSubmission === supportEnums.ENROLLMENT_REQUIRED
                                        ? Images.claimSubmitINotFill
                                        : Images.claimSubmitI
                                }
                                alt="checked"
                            />
                        )}
                        {(item?.payer?.transactionSupport?.dentalClaimSubmission === supportEnums.SUPPORTED ||
                            item?.payer?.transactionSupport?.dentalClaimSubmission === supportEnums.ENROLLMENT_REQUIRED) && (
                            <img
                                src={
                                    item?.payer?.transactionSupport?.dentalClaimSubmission === supportEnums.ENROLLMENT_REQUIRED
                                        ? Images.claimSubmitDNotFill
                                        : Images.claimSubmitD
                                }
                                alt="checked"
                            />
                        )}
                        {(item?.payer?.transactionSupport?.professionalClaimSubmission === supportEnums.SUPPORTED ||
                            item?.payer?.transactionSupport?.professionalClaimSubmission === supportEnums.ENROLLMENT_REQUIRED) && (
                            <img
                                src={
                                    item?.payer?.transactionSupport?.professionalClaimSubmission === supportEnums.ENROLLMENT_REQUIRED
                                        ? Images.claimSubmitPNotFill
                                        : Images.claimSubmitP
                                }
                                alt="checked"
                            />
                        )}
                    </div>
                ) : (
                    <div className="flex-align-center" style={{ height: '45px' }}>
                        <img src={Images.closeGray} alt="checked" />
                    </div>
                ),
            width: '13%',
        },
        {
            rowValue: (
                <div className="flex-align-center" style={{ height: '45px' }}>
                    <img
                        src={
                            item?.payer?.transactionSupport?.professionalClaimSubmission === supportEnums.NOT_SUPPORTED
                                ? Images.closeGray
                                : Images.checkCircleBlue
                        }
                        alt="checked"
                    />
                </div>
            ),
            width: '15%',
        },
        {
            rowValue: (
                <div className="flex-align-center" style={{ height: '45px' }}>
                    <img
                        src={
                            item?.payer?.transactionSupport?.claimPayment === supportEnums.SUPPORTED
                                ? Images.checkCircleBlue
                                : item?.payer?.transactionSupport?.claimPayment === supportEnums.ENROLLMENT_REQUIRED
                                  ? Images.checkCircleBlueTransparent
                                  : Images.closeGray
                        }
                        alt="checked"
                    />
                </div>
            ),
            width: '11%',
        },
        {
            rowValue: (
                <div className="flex-align-center" style={{ height: '45px' }}>
                    <img
                        src={
                            item?.payer?.transactionSupport?.unsolicitedClaimAttachment === supportEnums.SUPPORTED
                                ? Images.checkCircleBlue
                                : item?.payer?.transactionSupport?.unsolicitedClaimAttachment === supportEnums.ENROLLMENT_REQUIRED
                                  ? Images.checkCircleBlueTransparent
                                  : Images.closeGray
                        }
                        alt="checked"
                    />
                </div>
            ),
            width: '12%',
        },
        {
            rowValue: (
                <div className="flex-align-center" style={{ height: '45px' }}>
                    <img
                        src={
                            item?.payer?.transactionSupport?.claimStatus === supportEnums.SUPPORTED
                                ? Images.checkCircleBlue
                                : item?.payer?.transactionSupport?.claimStatus === supportEnums.ENROLLMENT_REQUIRED
                                  ? Images.checkCircleBlueTransparent
                                  : Images.closeGray
                        }
                        alt="checked"
                    />
                </div>
            ),
            width: '14%',
        },
    ];
};

export const fundingSourceServicesInfo = (handleClickButton, serviceChargeRates, open, currentId) => {
    return {
        loadingType: 'GET_FUNDING_SOURCE_SERVICE_BY_ID',

        head: [
            { name: 'serviceTypeId', rowName: 'name', title: 'Service', custom: false, width: '17%' },
            { name: 'cptCode', title: 'CPT Code', custom: false, width: '7%' },
            {
                name: 'type',
                title: 'Type',
                disabled: true,
                width: '7%',
                filterList: [
                    { name: 'All', id: 'All' },
                    { name: 'Direct', id: 'DIRECT' },
                    { name: 'Indirect', id: 'INDIRECT' },
                ],
            },
            { name: 'unitSize', title: 'Unit Size (min)', custom: false, width: '13%' },
            { name: 'min', title: 'Min Unit', custom: false, width: '10%' },
            { name: 'max', title: 'Max Unit', custom: false, width: '10%' },
            { name: 'diagnosticCodes', title: 'Diagnostic Codes', custom: false, width: '10%' },
            { name: 'noteTemplates', title: 'Template', custom: false, width: '15%' },
            {
                name: 'status',
                title: 'Status ',
                disabled: true,
                width: '8%',
                filterList: [
                    { name: 'All', id: 'All' },
                    { name: 'Active', id: serviceStatusEnums.ACTIVE },
                    { name: 'Inactive', id: serviceStatusEnums.INACTIVE },
                ],
            },
            RolePermission([PermissionList.FS_SERVICE_CREATE?.code]) && {
                name: 'action',
                title: 'Action',
                custom: false,
                disabled: true,
                width: '7%',
            },
        ],

        body: (item) => {
            return [
                { value: <TextRow name={item?.serviceTypeId?.name} textWidth={8} />, width: '17%' },
                { value: <TextRow name={item?.cptCode} />, width: '7%' },
                { value: <TypeRow text={item?.type ? item?.type : 'Not Set'} />, width: '7%' },
                { value: <TextRow name={item?.unitSize} />, width: '13%' },
                { value: <TextRow name={item?.min} />, width: '10%' },
                { value: <TextRow name={item?.max} />, width: '10%' },

                {
                    value: item?.diagnosticCodes?.length ? (
                        <div className="credential-item">
                            <div className="diagnostic-code-chip">{item?.diagnosticCodes[0]}</div>
                            {item?.diagnosticCodes?.length > 1 ? (
                                <SimpleTooltip
                                    title={
                                        <div>
                                            {item?.diagnosticCodes?.slice(1)?.map((item, k) => (
                                                <p key={k}>{item}</p>
                                            ))}
                                        </div>
                                    }
                                    placement="top-end"
                                >
                                    {item?.diagnosticCodes?.length > 1 ? <button>+{item?.diagnosticCodes?.length - 1}</button> : ''}
                                </SimpleTooltip>
                            ) : (
                                ''
                            )}
                            {item?.diagnosisMatchRequired && (
                                <SimpleTooltip title="Diagnosis match required: the client must have one of these codes to bill" placement="top-end">
                                    <img src={Images.lock} alt="match required" style={{ width: 16, height: 16, marginLeft: 4 }} />
                                </SimpleTooltip>
                            )}
                        </div>
                    ) : (
                        <p className="not-set">Not set</p>
                    ),
                    width: '10%',
                },
                {
                    value: item?.noteTemplates?.length ? (
                        <div className="flex-align-center full-width" style={{ gap: 6 }}>
                            <TextRow name={item.noteTemplates[0]?.title} />

                            {item.noteTemplates.length > 1 && (
                                <SimpleTooltip
                                    title={
                                        <div>
                                            {item.noteTemplates.slice(1).map((template) => (
                                                <p key={template?.id}>{template?.title}</p>
                                            ))}
                                        </div>
                                    }
                                    placement="top-end"
                                >
                                    <button className='table-tag-count-box'>+{item.noteTemplates.length - 1}</button>
                                </SimpleTooltip>
                            )}
                            {item?.onlyUseTemplate && (
                                <SimpleTooltip
                                    title={item.noteTemplates.length > 1 ? 'Notes for this service must use one of these templates' : 'This template is required for this charge rate'}
                                    placement="top-end"
                                >
                                    <img src={Images.lock} alt="lock" style={{ width: 16, height: 16, filter: 'brightness(0) saturate(100%) invert(43%) sepia(22%) saturate(373%) hue-rotate(182deg) brightness(95%) contrast(88%)' }} />
                                </SimpleTooltip>
                            )}
                        </div>
                    ) : (
                        <p className="not-set">Not set</p>
                    ),
                    width: '15%',
                },
                {
                    value: <StatusCard status={item?.active ? serviceStatusEnums.ACTIVE : serviceStatusEnums.INACTIVE} />,
                    width: '8%',
                },

                RolePermission([PermissionList.FS_SERVICE_CREATE?.code]) && {
                    value: (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            {RolePermission([PermissionList.FS_SERVICE_CREATE?.code]) && (
                                <EditWithIcon handleClick={(e) => handleClickButton(item, 'editFundingSourseService')} />
                            )}
                        </div>
                    ),
                    notClickable: true,
                    width: '7%',
                },
            ];
        },

        loadingBodyType: 'GET_FUNDING_MODIFIERS',

        renderBodyHeaderAction: (i) => {
            return (
                <div className="space-between" style={{ marginBottom: 12 }}>
                    <p className="charge-rate-title">Charge Rates</p>
                    {RolePermission([PermissionList.FS_SERVICE_CREATE?.code]) && (
                        <AddCircleBlue
                            text={'Add Charge Rate'}
                            handleClick={() => open(<FundingSourceModifiersAdd currentService={i} />)}
                        />
                    )}
                </div>
            );
        },

        renderAccordionRow: (i) => serviceChargeRates,

        accordionHead: [
            { title: 'Title', width: '13%' },
            { title: 'Modifier', width: '13%' },
            { title: 'Credentials', width: '14%' },
            { title: 'Billing Rate', width: '10%' },
            { title: 'Allowed Rate', width: '10%' },
            { title: 'Type', width: '10%' },
            { title: 'Default Paycode', width: '12%' },
            { title: 'Status', width: '13%' },
            { title: 'Action', width: '5%' },
        ],

        accordionBody: (item, index, parent) => [
            { value: <TextRow name={item?.name} />, width: '13%' },
            { value: <TextRow name={[item?.modifierTag, item?.modifierTag2, item?.modifierTag3].filter(Boolean).join(', ') || '--'} />, width: '13%' },
            {
                value: item?.credentials?.length ? (
                    <div className="credential-item">
                        <TextRow name={item?.credentials?.length ? item?.credentials[0]?.name : ''} textWidth={8} />
                        {item?.credentials?.length > 1 ? (
                            <SimpleTooltip
                                title={<ChargeRateCredentialsTooltip chargeRate={item} />}
                                placement="top-end"
                            >
                                {item?.credentials?.length > 1 ? <button>+{item?.credentials?.length - 1}</button> : ''}
                            </SimpleTooltip>
                        ) : (
                            ''
                        )}
                    </div>
                ) : (
                    <p className="not-set" style={{ whiteSpace: 'nowrap' }}>
                        Not set
                    </p>
                ),
                width: '14%',
            },
            { value: <PriceRow info={item?.billedRate ? item?.billedRate : 0} />, width: '10%' },
            { value: <PriceRow info={item?.allowedRate ? item?.allowedRate : 0} />, width: '10%' },
            { value: <TypeRow text={item?.type ? item?.type : 'Not Set'} />, width: '10%' },
            {
                value: <TextRow name={item?.payCodeTypeName ? item?.payCodeTypeName : 'Not Set'} />,
                width: '12%',
            },
            {
                value: (
                    <div className="flex-align-center" style={{ gap: 8 }}>
                        <StatusCard
                            status={item?.status === true ? serviceStatusEnums.ACTIVE : serviceStatusEnums.INACTIVE}
                            text={item?.status === false ? `: ${moment.utc(item?.inactivatedDate).format('MM/DD/YY')}` : ''}
                        />
                        {item?.status && (
                            <>
                                {currentId === item?.id ? (
                                    <MinLoader margin={'0'} color={Colors.ThemeBlue} position={'relative'} />
                                ) : (
                                    <CustomizedSwitch
                                        disabled={!RolePermission([PermissionList.FS_SERVICE_CREATE?.code])}
                                        checked={item?.status}
                                        handleClick={() => handleClickButton(item, 'changeStatus', parent)}
                                    />
                                )}
                            </>
                        )}
                    </div>
                ),
                width: '13%',
            },
            RolePermission([PermissionList.FS_SERVICE_CREATE?.code]) && {
                value: (
                    <button className="reset-btn-by-default" onClick={() => handleClickButton(item, 'editChargeRate', parent)}>
                        <img src={Images.edit} alt="edit" />
                    </button>
                ),
                width: '5%',
            },
        ],

        noDetailsText: 'No Charge Rates yet',
        notYet: {
            title: 'No Services Added',
            subTitle: 'Add services to this funding source.',
            image: Images.noService,
        },
    };
};

export const PayerEnrollmentItem = ({ title, value }) => {
    return (
        <div className="enrollment-item-box">
            <p>{title}</p>
            <span>{value}</span>
        </div>
    );
};
